import { useEffect, useRef, useState } from 'react';
import BackendService from '../../services/admin/BackendService';
import { redirectTo } from '../../utils/navigation';

export type HandoffStatus =
  | { kind: 'working' }
  | { kind: 'opening'; mobileRedirectUrl: string }
  | { kind: 'mismatch' }
  | { kind: 'notAdmin' }
  | { kind: 'invalid' }
  | { kind: 'missing' };

/**
 * Redeems the web-admin -> mobile-app handoff token once on mount. On success it
 * navigates straight to the app deep link; on failure it maps the backend error
 * to a status the landing page can render.
 */
export function useAppHandoffRedeem(ht: string | null): HandoffStatus {
  const [status, setStatus] = useState<HandoffStatus>({ kind: 'working' });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!ht) {
      setStatus({ kind: 'missing' });
      return;
    }

    new BackendService()
      .redeemImpersonationAppHandoff(ht)
      .then(({ mobileRedirectUrl }) => {
        setStatus({ kind: 'opening', mobileRedirectUrl });
        redirectTo(mobileRedirectUrl);
      })
      .catch((error: { code?: string; status?: number }) => {
        if (error?.code === 'HANDOFF_ACTOR_MISMATCH') {
          setStatus({ kind: 'mismatch' });
          return;
        }
        // The redeem route is admin-only. The admin and end-user apps share one
        // Auth0 tenant, so the phone scanning the QR may be signed in as an end
        // user; the backend rejects that with 403 ADMIN_ACCESS_REQUIRED (any
        // other 403 here means the same thing).
        if (error?.code === 'ADMIN_ACCESS_REQUIRED' || error?.status === 403) {
          setStatus({ kind: 'notAdmin' });
          return;
        }
        setStatus({ kind: 'invalid' });
      });
  }, [ht]);

  return status;
}
